import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { DepositType } from '@/interfaces/deposits';
import depositsServices from '@/api/depositService';

interface MetaData {
  totalPages: number;
  totalElements: number;
}

interface DepositsState {
  depositTypes: { [key: string]: DepositType[] };
  allDeposits: DepositType[];
  loading: boolean;
  currFilters: string;
  metaData: { [key: string]: MetaData };
}

const initialState: DepositsState = {
  depositTypes: {},
  allDeposits: [],
  loading: false,
  currFilters: '',
  metaData: {},
};

export const fetchDeposits = createAsyncThunk('deposits/fetch', async ({ size, page }: { size: number; page: number }, thunkAPI) => {
  try {
    const result = await depositsServices.getDeposits({ size, page: page - 1 });
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const depositsSlice = createSlice({
  name: 'deposits',
  initialState,
  reducers: {
    refresh: () => initialState,
    handleParamsChange: (state, action: PayloadAction<{ size: number; page: number }>) => {
      const filters = JSON.stringify(action.payload);
      state.currFilters = filters;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchDeposits.pending, state => {
      state.loading = true;
    });

    builder.addCase(fetchDeposits.fulfilled, (state, action) => {
      const { payload, meta } = action;
      const { size, page } = meta.arg;
      const stringifyFilters = JSON.stringify({ size, page });
      const resp = payload.data.map((type: DepositType) => ({ ...type, name: `${type.name} (${type.currency})`, title: type.name }));
      if (!state.allDeposits.length) {
        state.allDeposits = resp;
        const filters = JSON.stringify({ size: 5, page });
        state.depositTypes[filters] = resp.slice(0, 5);
        state.metaData[filters] = { totalPages: Math.ceil(+payload.totalElements / 5), totalElements: payload.totalElements };
      } else {
        state.depositTypes[stringifyFilters] = resp;
        state.metaData[stringifyFilters] = { totalPages: payload.totalPages, totalElements: payload.totalElements };
      }
      state.loading = false;
    });

    builder.addCase(fetchDeposits.rejected, state => {
      state.loading = false;
    });
  },
});

export const { refresh, handleParamsChange } = depositsSlice.actions;

export const selectDeposits = (state: RootState) => state.deposits;

export default depositsSlice.reducer;
